import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';

import { useUpdateAccountingExpense } from '@neo.tax/rd/hooks';
import { AccountingExpense } from '@neo.tax/rd/hooks/useAccountingExpense';
import { ToastConfigState } from '@neo.tax/shared';

import { AccountingExpenseGeneralFormType } from '../types';

interface UseAccountingExpenseGeneralFormProps {
  accountingExpense: AccountingExpense;
}

const makeDefaultFormValues = (accountingExpense: AccountingExpense): AccountingExpenseGeneralFormType => ({
  id: accountingExpense.id,
  displayName: accountingExpense.displayName ?? '',
  rdCategory: accountingExpense.accountingQre.rdCategory,
});

export const useAccountingExpenseGeneralForm = ({
  accountingExpense,
}: UseAccountingExpenseGeneralFormProps): UseFormReturn<AccountingExpenseGeneralFormType> => {
  const [updateAccountingExpense] = useUpdateAccountingExpense();

  const form = useForm<AccountingExpenseGeneralFormType>({
    mode: 'onChange',
    defaultValues: makeDefaultFormValues(accountingExpense),
  });
  const { handleSubmit, watch } = form;
  const currentAccountingExpenseId = watch('id');

  const onValid: SubmitHandler<AccountingExpenseGeneralFormType> = useCallback(
    (values: AccountingExpenseGeneralFormType): ReturnType<typeof updateAccountingExpense> =>
      updateAccountingExpense(accountingExpense.id, Object.assign(values, { id: undefined })),
    [accountingExpense.id, updateAccountingExpense],
  );

  const onInvalid = (): void => {
    ToastConfigState.pushErrorToast('Error updating expense', {
      description: 'One or more values in the form are invalid. Please correct them to save.',
      duration: 'MEDIUM',
    });
  };
  const onInvalidDebounced = useDebouncedCallback(onInvalid, 500);

  useEffect(() => {
    const subscription = watch(() => handleSubmit(onValid, onInvalidDebounced)());
    return (): void => subscription.unsubscribe();
  }, [handleSubmit, onInvalidDebounced, onValid, watch]);

  useEffect(() => {
    if (currentAccountingExpenseId !== accountingExpense.id) {
      form.reset(makeDefaultFormValues(accountingExpense));
    }
    // We only want to reset the form when the accountingExpense changes
  }, [currentAccountingExpenseId, accountingExpense, form]);

  return form;
};
